import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader } from './ui/Card';
import { api } from '../lib/api';

// Stable keys + accountant-facing labels for the posting map. Labels are
// standard accounting terms (kept in English; the JP code is the value).
const FIELDS: Array<{ key: string; label: string }> = [
  { key: 'ap_expense', label: 'AP — expense (Dr)' },
  { key: 'ap_payable', label: 'AP — payable 買掛金 (Cr)' },
  { key: 'ar_receivable', label: 'AR — receivable 売掛金 (Dr)' },
  { key: 'ar_revenue', label: 'AR — revenue 売上高 (Cr)' },
  { key: 'ar_tax', label: 'AR — tax 預り消費税 (Cr)' },
  { key: 'ar_cash', label: 'AR payment — cash 現金 (Dr)' },
  { key: 'exp_expense', label: 'Expense — cost (Dr)' },
  { key: 'exp_payable', label: 'Expense — payable 未払金 (Cr)' },
  { key: 'pay_payable', label: 'Payment — payable settled (Dr)' },
  { key: 'pay_cash', label: 'Payment — cash outflow (Cr)' },
];

const inputCn = 'w-28 px-2 py-1 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num focus:outline-none focus:ring-2 focus:ring-accent-500';

/** Configure which GL codes auto-posting uses, per posting kind. */
export function PostingAccountsCard() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const map = useQuery<Record<string, string>>({
    queryKey: ['posting-accounts', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/ledger/posting-accounts`)).data,
  });

  useEffect(() => { if (map.data) setDraft(map.data); }, [map.data]);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/workspaces/${wsId}/ledger/posting-accounts`, draft)).data,
    onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2500); qc.invalidateQueries({ queryKey: ['posting-accounts', wsId] }); },
  });

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="font-medium">{t('settings.posting.title')}</span>
          <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
            {t('common.save')}{saved ? ' ✓' : ''}
          </button>
        </div>
        <p className="text-xs text-ink-500 mt-1">{t('settings.posting.subtitle')}</p>
      </CardHeader>
      <CardBody>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {FIELDS.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-2">
              <span className="text-sm text-ink-600 dark:text-ink-300">{f.label}</span>
              <input
                value={draft[f.key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                className={inputCn}
                placeholder={map.data?.[f.key]}
              />
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}
